import _ from 'lodash'
import qs from 'qs'

import type {ConsultationType} from '@/content/bookConsultation'
import {countryCards} from '@/content/countryCards'
import type {LawFirmsServiceId} from '@/content/lawFirms'
import {RegistrationProcessTabId} from '@/utils/admin/consts'
import type {AuthActionType} from '@/utils/user/constants'

import type {BrandSubscriptionTier} from './brand-subscription/constants'
import type {SendEmailData} from './brevo/types'
import type {EmailFeedback} from './constants'
import {Locale} from './constants'
import type {IndustryName, IndustryTopicName} from './contentful'
import type {Jurisdiction} from './countries/constants'
import type {IndustryGuideType} from './industry-guide/constants'
import type {NotificationWithTemplate} from './notifications/emails/constants'
import type {RegistrationPoaTemplateId} from './power-of-attorney/types'
import type {PricingTabType} from './pricing/constants'
import type {Service} from './service-request/types'
import {TrademarkNumberType} from './trademark/numberType'
import type {ProfileTab, TrademarkDetailActionType} from './user-portal/types'

const defaultHost = process.env.DEFAULT_HOST ?? process.env.defaultHost

// The canonical public origin. Must stay in sync with DEFAULT_HOST in docker/app/prod.env —
// anything that advertises URLs to search engines (canonical tags, sitemap) has to agree on
// the host, or Google treats the mismatching ones as non-canonical duplicates.
export const PRODUCTION_ORIGIN = 'https://www.tramatm.com'

export const DEFAULT_HOST = defaultHost || PRODUCTION_ORIGIN

export const routeTo = {
  aboutUs: {
    home: () => '/about-us',
    teamMember: (teamMember: string) => `/about-us/${teamMember}`,
  },
  admin: {
    page: (path: string, queryStringData?: any) =>
      `/admin#${path}${qs.stringify(queryStringData, {addQueryPrefix: true})}`,
    home: () => '/admin',
    login: () => '/admin/login',
    registrationRequest: {
      show: (id: string) => `/registration_request/${id}/show`,
      drafting: {
        show: (id: string) =>
          `/registration_request/${id}/show/${RegistrationProcessTabId.drafting}`,
      },
      filing: {
        show: (id: string) => `/registration_request/${id}/show/${RegistrationProcessTabId.filing}`,
      },
    },
    serviceRequest: {
      show: (id: string) => `/service_request/${id}/show`,
    },
    trademark: {
      list: () => '/trademark',
      create: (queryStringData?: any) =>
        `/trademark/create${qs.stringify(queryStringData, {addQueryPrefix: true})}`,
      show: (id: string) => `/trademark/${id}/show`,
    },
    officeAction: {
      create: (trademarkId: string) => `office_action/create/${trademarkId}`,
      show: (officeActionId: string) => `/office_action/${officeActionId}/show`,
      examination: (officeActionId: string) => `/office_action/${officeActionId}/examination`,
      additionalInfo: (officeActionId: string) =>
        `/office_action/${officeActionId}/additional-info`,
      docketing: (officeActionId: string) => `/office_action/${officeActionId}/docketing`,
      list: () => `/office_action`,
    },
    supportTicket: {
      show: (id: number) => `/support_ticket/${id}/show`,
      edit: (id: number) => `/support_ticket/${id}`,
    },
    user: {
      list: () => '/user',
      detail: (id: string) => `/user/${id}/show`,
      orders: (id: string) => `/user/${id}/show/orders`,
    },
    dev: {
      task: {
        show: (id: string) => `/task/${id}/show`,
      },
    },
    consultation: {
      show: (id: string) => `/consultation/${id}/show`,
    },
    brandMonitoringTask: {
      show: (id: string) => `/brand_monitoring_task/${id}/show`,
    },
    brandSubscription: {
      show: (id: string) => `/brand_subscription/${id}/show`,
    },
    caseRequest: {
      show: (id: string) => `/case_request/${id}/show`,
    },
    case: {
      show: (id: string) => `/case/${id}/show`,
      create: (queryStringData?: any) =>
        `/case/create${qs.stringify(queryStringData, {addQueryPrefix: true})}`,
    },
    service: {
      show: (id: string) => `/service/${id}/show`,
    },
    useInCommerceRequest: {
      show: (id: string) => `/use_in_commerce_request/${id}/show`,
    },
    refundRequest: {
      create: (queryStringData?: any) =>
        `/refund_request/create${qs.stringify(queryStringData, {addQueryPrefix: true})}`,
      show: (id: string) => `/refund_request/${id}/show`,
    },
    creditNote: {
      show: (id: number) => `/credit_note/${id}/show`,
    },
    verificationResult: {
      edit: (id: string) => `/verification_result/${id}`,
    },
  },
  api: {
    country: () => '/api/country',
    aircall: {
      search: (phoneNumber: string) =>
        `/api/aircall${qs.stringify({phoneNumber}, {addQueryPrefix: true})}`,
    },
    application: {
      comments: (id: string) => `/api/application/${id}/thread-comment`,
      createThreadComment: (id: string) => `/api/application/${id}/thread-comment`,
      updateThreadComment: (id: string, threadCommentId: string) =>
        `/api/application/${id}/thread-comment/${threadCommentId}`,
      clone: (id: string) => `/api/application/${id}/clone`,
      notify: (id: string) => `/api/application/${id}/notify`,
      onboardingFinished: (id: string) => `/api/application/${id}/onboarding-finished`,
      parsePartnerApplication: (id: string) => `/api/application/${id}/parse-partner-application`,
    },
    dev: {
      export: {
        invoiceEntity: {
          xml: () => '/api/dev/export/invoice-entity/xml',
        },
        filingConfirmations: {
          zip: () => '/api/dev/export/filing-confirmations/zip',
        },
      },
      email: (
        emailType: NotificationWithTemplate,
        locale?: string,
        notificationEntityId?: string,
        emailData?: Partial<SendEmailData>,
        previewEnabled?: boolean,
      ) =>
        `/api/dev/email/${emailType}${qs.stringify(
          {
            locale,
            notificationEntityId,
            ...(emailData ? {emailData: JSON.stringify(emailData)} : {}),
            previewEnabled,
          },
          {addQueryPrefix: true},
        )}`,
    },
    user: {
      me: () => '/api/user/me',
      signup: () => '/api/user/signup',
      resetPassword: () => '/api/user/reset-password',
      setPassword: () => '/api/user/set-password',
      loginByMagicLinkRequest: () => '/api/user/login-by-magic-link-request',
      get: (params: {email: string; exceptUserId?: string; includeHistory?: boolean}) =>
        `/api/user${qs.stringify(params, {addQueryPrefix: true})}`,
      create: () => '/api/user',
      activate: () => '/api/user/activate',
      initiate: () => '/api/user/initiate',
      changePassword: () => '/api/user/password',
      hasMonitoringTrial: () => '/api/user/has-monitoring-trial',
      assignedAdmin: (id: string) => `/api/user/${id}/assigned-admin`,
      deactivate: (id: string) => `/api/user/${id}/deactivate`,
      update: (id: string) => `/api/user/${id}`,
      initiationLink: (id: string) => `api/user/${id}/initiation-link`,
      entity: {
        create: (userId: string) => `/api/user/${userId}/entity`,
        update: (userId: string, entityId: string) => `/api/user/${userId}/entity/${entityId}`,
      },
      note: {
        create: (id: string) => `/api/user/${id}/note`,
        id: (id: string, noteId: number) => `/api/user/${id}/note/${noteId}`,
      },
      tracking: (id: string) => `/api/user/${id}/tracking`,
    },
    consultation: {
      assignedAdmin: (id: number) => `/api/consultation/${id}/assigned-admin`,
      note: {
        create: (id: string) => `/api/consultation/${id}/note`,
        id: (id: string, noteId: number) => `/api/consultation/${id}/note/${noteId}`,
      },
      generateOutcomeEmail: (id: string) => `/api/consultation/${id}/generate-outcome-email`,
    },
    contentful: {
      blog: {
        latest: (locale: Locale) =>
          `/api/contentful/blog/latest${qs.stringify({locale}, {addQueryPrefix: true})}`,
      },
    },
    document: {
      upload: (hub_id: string) => `/api/document-hub/${hub_id}/document`,
      id: {
        index: (hub_id: string, id: number) => `/api/document-hub/${hub_id}/document/${id}`,
        url: (hub_id: string, id: number) => `/api/document-hub/${hub_id}/document/${id}/url`,
      },
      url: () => `/api/document/url`,
    },
    task: {
      get: (id: string) => `/api/task/${id}`,
      repeat: (id: string) => `/api/task/${id}/repeat`,
    },
    brand: {
      admin: {
        create: () => '/api/admin/brand',
        edit: (id: string) => `/api/admin/brand/${id}`,
        analyseAi: () => `/api/admin/brand/analyse-ai`,
      },
      create: () => '/api/brand',
      id: (id: string) => `/api/brand/${id}`,
      assignItems: () => `/api/brand/assign-items`,
    },
    brandSubscription: {
      create: (proactive?: boolean) =>
        `/api/brand-subscription${qs.stringify({proactive}, {addQueryPrefix: true})}`,
      update: (id: string) => `/api/brand-subscription/${id}`,
      note: {
        create: (id: string) => `/api/brand-subscription/${id}/note`,
        id: (id: string, noteId: number) => `/api/brand-subscription/${id}/note/${noteId}`,
      },
      debug: {
        status: (id: string) => `/api/brand-subscription/${id}/debug/status`,
      },
      payment: {
        debug: {
          notify: (id: string) => `/api/brand-subscription/${id}/payment/debug/notify`,
          prepare: (id: string) => `/api/brand-subscription/${id}/payment/debug/prepare`,
          charge: (id: string) => `/api/brand-subscription/${id}/payment/debug/charge`,
          confirm: (id: string) => `/api/brand-subscription/${id}/payment/debug/confirm`,
          decline: (id: string) => `/api/brand-subscription/${id}/payment/debug/decline`,
        },
      },
      cancel: (id: string) => `/api/brand-subscription/${id}/cancel`,
      notify: (id: string) => `/api/brand-subscription/${id}/notify`,
      tracking: (id: string) => `/api/brand-subscription/${id}/tracking`,
      billingEntity: (id: string) => `/api/brand-subscription/${id}/billing-entity`,
      coupon: (subscriptionId: string) =>
        `/api/brand-subscription/${subscriptionId}/discount-coupon`,
      applyCoupon: (subscriptionId: string) =>
        `/api/brand-subscription/${subscriptionId}/discount-coupon`,
    },
    brandMonitoringTask: {
      notify: (id: string) => `/api/brand-monitoring-task/${id}/notify`,
      assignedAdmin: (id: string) => `/api/brand-monitoring-task/${id}/assigned-admin`,
      tracking: (id: string) => `/api/brand-monitoring-task/${id}/tracking`,
    },
    request: {
      sendDocuments: (id: string) => `/api/request/${id}/send-documents`,
      update: (id: string) => `/api/request/${id}`,
      create: (amazonBrandRegistry?: boolean) =>
        `/api/request${qs.stringify({amazonBrandRegistry}, {addQueryPrefix: true})}`,
      changeType: (id: string) => `/api/request/${id}/change-type`,
      coupon: (requestId: string) => `/api/request/${requestId}/discount-coupon`,
      applyCoupon: (requestId: string) => `/api/request/${requestId}/discount-coupon`,
      summaryLink: (id: string) => `/api/request/${id}/summary-link`,
      goodsAndServices: (id: string) => `/api/request/${id}/goods-and-services`,
      status: (id: string) => `/api/request/${id}/status`,
      applyE2eProtection: (id: string) => `/api/request/${id}/end-to-end-protection`,
      generateApplication: (id: string) => `/api/request/${id}/generate-application`,
      assignedAdmin: {
        index: (id: string) => `/api/request/${id}/assigned-admin`,
        suggestion: (id: string) => `/api/request/${id}/assigned-admin/suggestion`,
      },
      note: {
        create: (id: string) => `/api/request/${id}/note`,
        id: (id: string, noteId: number) => `/api/request/${id}/note/${noteId}`,
      },
      brandProtection: (id: string) => `/api/request/${id}/brand-protection`,
      application: {
        saveDraft: (id: string) => `/api/request/${id}/application/draft`,
        update: (id: string) => `/api/request/${id}/application`,
        goodsAndServices: (id: string) => `/api/request/${id}/application/goods-and-services`,
        saveUseInCommerce: (id: string) => `/api/request/${id}/application/use-in-commerce`,
        logo: {
          preview: (id: string, options: Record<string, any>) =>
            `/api/request/${id}/application/logo/preview?${qs.stringify(options)}`,
          generate: (id: string) => `/api/request/${id}/application/logo/generate`,
        },
        ai: {
          pipeline: (id: string) => `/api/request/${id}/application/ai/pipeline`,
        },
        document: {
          poa: (id: string) => `/api/request/${id}/application/document/poa`,
          poaPreview: (
            id: string,
            templateId?: RegistrationPoaTemplateId,
            registrationEntity?: any,
          ) =>
            `/api/request/${id}/application/document/poa-preview${qs.stringify(
              {
                templateId,
                registrationEntity: registrationEntity ? JSON.stringify(registrationEntity) : null,
              },
              {
                addQueryPrefix: true,
                skipNulls: true,
              },
            )}`,
        },
        specimen: {
          create: (id: string) => `/api/request/${id}/application/specimen`,
          update: (id: string, specimenId: string) =>
            `/api/request/${id}/application/specimen/${specimenId}`,
          copy: (id: string, specimenId: string) =>
            `/api/request/${id}/application/specimen/${specimenId}`,
          delete: (id: string, specimenId: string) =>
            `/api/request/${id}/application/specimen/${specimenId}`,
          resize: (id: string, specimenId: string) =>
            `/api/request/${id}/application/specimen/${specimenId}/resize`,
        },
      },
      reportViolation: (id: string) => `/api/request/${id}/violation`,
      notify: (id: string) => `/api/request/${id}/notify`,
      updateType: (id: string) => `/api/request/${id}/type`,
      logoUrl: (id: string) => `/api/request/${id}/logo-url`,
      exportLogo: (id: string) => `/api/request/${id}/logo`,
      debug: {
        notify: (id: string) => `/api/request/${id}/debug/notify`,
      },
      ai: (id: string) => `/api/request/${id}/ai`,
      triggerAiGsClassItems: (id: string) => `/api/request/${id}/ai/gs-class-items`,
      aiPipeline: (id: string) => `/api/request/${id}/ai/pipeline`,
      tracking: (id: string) => `/api/request/${id}/tracking`,
    },
    product: {
      invoice: {
        document: (id: string) => `/api/product/${id}/invoice/document`,
      },
      update: (id: string) => `/api/product/${id}`,
      finishOrder: (id: string) => `/api/product/${id}/finish-order`,
      regenerateDocuments: (id: string) => `/api/product/${id}/regenerate-document`,
      processPayment: (id: string) => `/api/product/${id}/process-payment`,
      preview: (id: string, type: string, country?: string) =>
        `/api/product/${id}/preview-document${qs.stringify(
          {type, country},
          {addQueryPrefix: true, skipNulls: true},
        )}`,
      pay: (id: string) => `/api/product/${id}/pay`,
    },
    discountCoupon: {
      verify: (id: string) => `/api/discount-coupon/${id}/verify`,
    },
    quoteHub: {
      quote: (hubId: string) => `/api/quote-hub/${hubId}/quote`,
    },
    serviceRequest: {
      admin: {
        create: () => `/api/service-request/admin`,
        edit: (id: string) => `/api/service-request/admin/${id}`,
      },
      create: (serviceType?: Service) =>
        `/api/service-request${
          serviceType ? qs.stringify({serviceType}, {addQueryPrefix: true}) : ''
        }`,
      clone: (id: string) => `/api/service-request/${id}/clone`,
      checkout: () => '/api/service-request/checkout',
      update: (id: string) => `/api/service-request/${id}`,
      trademarkRenewal: () => '/api/service-request/trademark-renewal',
      trademarkOwnershipTransfer: () => '/api/service-request/trademark-ownership-transfer',
      changeStatus: (id: string) => `/api/service-request/${id}/status`,
      notify: (id: string) => `/api/service-request/${id}/notify`,
      assignedAdmin: (id: string) => `/api/service-request/${id}/assigned-admin`,
      coupon: (id: string) => `/api/service-request/${id}/discount-coupon`,
      note: {
        create: (id: string) => `/api/service-request/${id}/note`,
        id: (id: string, noteId: number) => `/api/service-request/${id}/note/${noteId}`,
      },
      sendInternalNotification: (id: string) =>
        `/api/service-request/${id}/send-internal-notification`,
    },
    trademark: {
      create: () => `/api/trademark`,
      edit: (id: string) => `/api/trademark/${id}`,
      add: () => `/api/trademark/add`,
      filing: {
        getData: (id: string) => `/api/trademark/${id}/filing`,
        update: (id: string) => `/api/trademark/${id}/filing`,
      },
      logoUrl: (id: string) => `/api/trademark/${id}/logo-url`,
      euipoFiling: {
        preview: (id: string) => `/api/trademark/${id}/filing/euipo/preview`,
      },
      auipoFiling: {
        preview: (id: string) => `/api/trademark/${id}/filing/auipo/preview`,
        contacts: (id: string) => `/api/trademark/${id}/filing/auipo/contacts`,
        validateAddress: (id: string) => `/api/trademark/${id}/filing/auipo/validate-address`,
      },
      officeAction: {
        create: (id: string) => `/api/trademark/${id}/office-action`,
      },
      notify: (id: string) => `/api/trademark/${id}/notify`,
      recordSummary: (id: string) => `/api/trademark/${id}/record-summary`,
      assignedAdmin: (id: number) => `/api/trademark/${id}/assigned-admin`,
      note: {
        create: (id: string) => `/api/trademark/${id}/note`,
        id: (id: string, noteId: number) => `/api/trademark/${id}/note/${noteId}`,
      },
      tracking: (id: string) => `/api/trademark/${id}/tracking`,
      relatedJurisdictions: (id: string) => `/api/trademark/${id}/related-jurisdictions`,
      extendCountry: (id: string) => `/api/trademark/${id}/extend-country`,
      extendClass: (id: string) => `/api/trademark/${id}/extend-class`,
      bulkMarkAsPaidToPartner: () => `/api/trademark/paid-to-partner`,
      similarTrademarksNotice: (id: string) => `/api/trademark/${id}/similar-trademarks-notice`,
      partnerFiling: (id: string) => `/api/trademark/${id}/filing/partner`,
      poaDocument: {
        generate: () => `/api/trademark/poa-document/generate`,
        upload: () => `/api/trademark/poa-document/upload`,
        delete: (id: number) => `/api/trademark/poa-document/${id}`,
      },
      trademarkSharedDocument: {
        detach: (id: string, documentId: number) =>
          `/api/trademark/${id}/trademark-shared-document/${documentId}`,
      },
    },
    trademarkClass: {
      specimen: {
        create: (id: string) => `/api/trademark-class/${id}/specimen`,
        update: (id: string, specimenId: string) =>
          `/api/trademark-class/${id}/specimen/${specimenId}`,
        copy: (id: string, specimenId: string) =>
          `/api/trademark-class/${id}/specimen/${specimenId}`,
        delete: (id: string, specimenId: string) =>
          `/api/trademark-class/${id}/specimen/${specimenId}`,
        resize: (id: string, specimenId: string) =>
          `/api/trademark-class/${id}/specimen/${specimenId}/resize`,
      },
    },
    trademarkSearch: {
      index: () => `/api/trademark-search`,
    },
    trademarkData: {
      get: (
        trademarkNumber: string,
        jurisdiction: Jurisdiction,
        trademarkNumberType = TrademarkNumberType.number,
        includeLogo?: boolean,
      ) =>
        `/api/trademark-data${qs.stringify(
          {
            trademarkNumber,
            trademarkNumberType,
            jurisdiction,
            ...(includeLogo && {includeLogo: 'true'}),
          },
          {addQueryPrefix: true},
        )}`,
    },
    officeAction: {
      create: () => '/api/office-action',
      onDemandAnalysis: () => '/api/office-action/on-demand-analysis',
      onDemandAnalysisByOfficeActionId: (id: string) =>
        `/api/office-action/${id}/on-demand-analysis`,
      saveExaminationDraft: (id: string) => `/api/office-action/${id}/examination-draft`,
      saveExamination: (id: string) => `/api/office-action/${id}/examination`,
      saveDocketing: (id: string) => `/api/office-action/${id}/docketing`,
      notify: (id: string) => `/api/office-action/${id}/notify`,
      order: {
        create: (id: string) => `/api/office-action/${id}/order`,
      },
      abandon: (id: string) => `/api/office-action/${id}/abandon`,
      additionalInfo: (id: string) => `/api/office-action/${id}/additional-info`,
      assignedAdmin: (id: string) => `/api/office-action/${id}/assigned-admin`,
      note: {
        create: (id: string) => `/api/office-action/${id}/note`,
        id: (id: string, noteId: number) => `/api/office-action/${id}/note/${noteId}`,
      },
      debug: {
        notify: (id: string) => `/api/office-action/${id}/debug/notify`,
        expire: (id: string) => `/api/office-action/${id}/debug/expire`,
      },
      acknowledgeSuspension: (id: string) => `/api/office-action/${id}/acknowledge-suspension`,
      freeSolution: (id: string) => `/api/office-action/${id}/free-solution`,
      tracking: (id: string) => `/api/office-action/${id}/tracking`,
    },
    invoice: {
      creditNote: (invoiceReference: string) => `/api/invoice/${invoiceReference}/credit-note`,
      document: (id: string) => `/api/invoice/${id}/document`,
      download: (id: string) => `/api/invoice/${id}/download`,
      getOneTranslated: (variableSymbol: string) =>
        `/api/invoice/invoice-by-reference/${variableSymbol}/translated`,
    },
    creditNote: {
      document: (id: string) => `/api/credit-note/${id}/document`,
    },
    search: {
      search: () => '/api/search',
      goodsAndServices: () => '/api/search/goods-and-services',
      goodsAndServicesPackages: () => '/api/search/goods-and-services-packages',
      euipo: {
        applicant: () => '/api/search/euipo/applicant',
      },
    },
    log: () => '/api/log-9a67b7c2-d7b9-4437-8544-c3be3965794d',
    supportTicket: {
      update: (id: number) => `/api/support-ticket/${id}`,
      assignedAdmin: (id: number) => `/api/support-ticket/${id}/assigned-admin`,
      note: {
        create: (id: string) => `/api/support-ticket/${id}/note`,
        id: (id: string, noteId: number) => `/api/support-ticket/${id}/note/${noteId}`,
      },
    },
    userPaymentMethod: {
      index: () => '/api/user-payment-method',
      id: {
        index: (id: string) => `/api/user-payment-method/${id}`,
        validate: (id: string) => `/api/user-payment-method/${id}/validate`,
      },
    },
    case: {
      create: () => '/api/case',
      update: (id: string) => `/api/case/${id}`,
      notify: (id: string) => `/api/case/${id}/notify`,
      assignedAdmin: (id: string) => `/api/case/${id}/assigned-admin`,
      dismiss: (id: string) => `/api/case/${id}/dismiss`,
      restore: (id: string) => `/api/case/${id}/restore`,
      note: {
        create: (id: string) => `/api/case/${id}/note`,
        id: (id: string, noteId: number) => `/api/case/${id}/note/${noteId}`,
      },
      solution: {
        order: {
          create: (caseId: string, solutionId: string) =>
            `/api/case/${caseId}/solution/${solutionId}/order`,
        },
      },
      tracking: (id: string) => `/api/case/${id}/tracking`,
    },
    caseRequest: {
      create: (type: string) => `/api/case-request${qs.stringify({type}, {addQueryPrefix: true})}`,
      update: (id: string) => `/api/case-request/${id}`,
      assignedAdmin: (id: string) => `/api/case-request/${id}/assigned-admin`,
      notify: (id: string) => `/api/case-request/${id}/notify`,
      note: {
        create: (id: string) => `/api/case-request/${id}/note`,
        id: (id: string, noteId: number) => `/api/case-request/${id}/note/${noteId}`,
      },
      tracking: (id: string) => `/api/case-request/${id}/tracking`,
    },
    caseCounterparty: {
      update: (id: string) => `/api/case-counterparty/${id}`,
    },
    service: {
      assignedAdmin: (id: string) => `/api/service/${id}/assigned-admin`,
      notify: (id: string) => `/api/service/${id}/notify`,
      tracking: (id: string) => `/api/service/${id}/tracking`,
      note: {
        create: (id: string) => `/api/service/${id}/note`,
        id: (id: string, noteId: number) => `/api/service/${id}/note/${noteId}`,
      },
    },
    useInCommerceRequest: {
      create: () => `/api/use-in-commerce-request`,
      update: (id: string) => `/api/use-in-commerce-request/${id}`,
      saveDraft: (id: string) => `/api/use-in-commerce-request/${id}/draft`,
      order: (id: string) => `/api/use-in-commerce-request/${id}/order`,
      proceed: (id: string) => `/api/use-in-commerce-request/${id}/proceed`,
      abandon: (id: string) => `/api/use-in-commerce-request/${id}/abandon`,
      confirmDeadline: (id: string) => `/api/use-in-commerce-request/${id}/confirm-deadline`,
      assignedAdmin: (id: string) => `/api/use-in-commerce-request/${id}/assigned-admin`,
      getComments: (id: string) => `/api/use-in-commerce-request/${id}/thread-comment`,
      createThreadComment: (id: string) => `/api/use-in-commerce-request/${id}/thread-comment`,
      updateThreadComment: (id: string, threadCommentId: string) =>
        `/api/use-in-commerce-request/${id}/thread-comment/${threadCommentId}`,
      filing: {getData: (id: string) => `/api/use-in-commerce-request/${id}/filing`},
      note: {
        create: (id: string) => `/api/use-in-commerce-request/${id}/note`,
        id: (id: string, noteId: number) => `/api/use-in-commerce-request/${id}/note/${noteId}`,
      },
      notify: (id: string) => `/api/use-in-commerce-request/${id}/notify`,
      debug: {
        abandonAuto: (id: string) => `/api/use-in-commerce-request/${id}/debug/abandon-auto`,
        applyLateFee: (id: string) => `/api/use-in-commerce-request/${id}/debug/apply-late-fee`,
        applyGracePeriodLateFee: (id: string) =>
          `/api/use-in-commerce-request/${id}/debug/apply-grace-period-late-fee`,
        initGracePeriod: (id: string) =>
          `/api/use-in-commerce-request/${id}/debug/init-grace-period`,
      },
      tracking: (id: string) => `/api/use-in-commerce-request/${id}/tracking`,
    },
    revolutPayment: {
      state: (paymentId: string) => `/api/revolut-payment/${paymentId}/state`,
    },
    docketing: {
      retry: () => `/api/docketing/retry`,
    },
    refundRequest: {
      create: () => '/api/refund-request',
      edit: (id: string) => `/api/refund-request/${id}`,
      assignedAdmin: (id: string) => `/api/refund-request/${id}/assigned-admin`,
      note: {
        create: (id: string) => `/api/refund-request/${id}/note`,
        id: (id: string, noteId: number) => `/api/refund-request/${id}/note/${noteId}`,
      },
      tracking: (id: string) => `/api/refund-request/${id}/tracking`,
    },
    writing: {
      improve: () => '/api/writing/improve',
    },
    externalTrademark: {
      logoUrl: (id: string) => `/api/external-trademark/${id}/logo-url`,
    },
    verificationResult: {
      edit: (id: string) => `/api/verification-result/${id}`,
    },
    reminder: {
      create: () => '/api/reminder',
      update: (id: string) => `/api/reminder/${id}`,
      delete: (id: string) => `/api/reminder/${id}`,
      deleteMany: () => '/api/reminder',
    },
    goodsAndServices: {
      getSingle: (jurisdiction?: Jurisdiction) =>
        `/api/goods-and-services${qs.stringify(
          {jurisdiction},
          {addQueryPrefix: true, skipNulls: true},
        )}`,
      getMultiple: (jurisdictions?: Jurisdiction[]) =>
        `/api/goods-and-services${qs.stringify(
          {jurisdictions: jurisdictions?.join(',')},
          {addQueryPrefix: true, skipNulls: true},
        )}`,
    },
  },
  blog: {
    home: () => '/blog',
    article: (slug: string) => `/blog/${slug}`,
    industry: (industry: IndustryName) => `/blog/category/${industry}`,
    industryArticle: (industry: IndustryName, slug: string) => `/blog/category/${industry}/${slug}`,
    industryTopic: (industryTopic: IndustryTopicName) => `/blog/topic/${industryTopic}`,
    industryTopicArticle: (industryTopic: IndustryTopicName, slug: string) =>
      `/blog/topic/${industryTopic}/${slug}`,
  },
  cookies: () => '/cookies',
  faq: () => '/faq',
  gdpr: () => '/gdpr',
  pricing: (tab?: PricingTabType) => `/pricing${tab ? `/${tab}` : ''}`,
  home: () => '/',
  // this is just home with extra options, did not want to pollute home call nad it may change in the future
  login: (openModal?: AuthActionType, callbackUrl?: string) =>
    `/${openModal ? qs.stringify({user: openModal, callbackUrl}, {addQueryPrefix: true}) : ''}`,
  verificationSummary: (id: string, result_id?: string) =>
    `/verification-summary/${result_id ? [id, result_id].join('/') : id}`,
  registrationRequest: {
    form: (id: string) => `/registration-request/${id}`,
    success: (id: string) => `/registration-request/${id}/success`,
  },
  applicationDraft: (id: string) => `/application-draft/${id}`,
  termsAndConditions: () => '/terms-and-conditions',
  accountTermsAndConditions: () => '/account-terms-and-conditions',
  portal: {
    home: (params?: Record<string, any>) =>
      `/portal${
        !_.isEmpty(_.omitBy(params, _.isNil)) ? qs.stringify(params, {addQueryPrefix: true}) : ''
      }`,
    login: () => '/portal/login',
    loginByMagicLink: () => `/portal/login-by-magic-link`,
    profile: (query?: {tab?: ProfileTab} & Record<string, any>) =>
      `/portal/profile${query ? qs.stringify(query, {addQueryPrefix: true}) : ''}`,
    setPassword: () => '/portal/set-password',
    forgotPassword: () => '/portal/forgot-password',
    successForgotPassword: () => '/portal/success-forgot-password',
    activate: (secret: string) => `/portal/activate/${secret}`,
    initiate: () => '/portal/activate',
    serviceOrder: (id: string) => `/portal/service-order/${id}`,
    noAccess: () => '/portal/no-access',
    solutionCenter: (useCase: string) => `/portal/solution-center/${useCase}`,
    trademark: {
      list: () => '/portal/trademarks',
      detail: (id: string, backUrl?: string, action?: TrademarkDetailActionType) =>
        `/portal/trademarks/${id}${qs.stringify({backUrl, action}, {addQueryPrefix: true})}`,
    },
    order: {
      list: () => '/portal/orders',
      detail: (id: string, backUrl?: string, query?: Record<string, any>) =>
        `/portal/order/${id}${qs.stringify({backUrl, ...query}, {addQueryPrefix: true})}`,
    },
    brand: {
      list: () => '/portal/brands',
      detail: (id: string) => `/portal/brands/${id}`,
    },
    monitoring: {
      list: () => '/portal/monitoring',
      detail: (id: string) => `/portal/monitoring/${id}`,
    },
    case: {
      list: () => '/portal/cases',
      detail: (id: string) => `/portal/cases/${id}`,
    },
    reportThreat: () => '/portal/report-threat',
    services: () => '/portal/services',
    verificationSummary: (id: string, resultId?: string, backUrl?: string) =>
      `/portal/verification-summary/${id}${resultId ? `/${resultId}` : ''}${
        backUrl ? qs.stringify({backUrl}, {addQueryPrefix: true}) : ''
      }`,
    successAdvancedProtectionPlan: (caseSolutionId: string) =>
      `/portal/success-advanced-protection-plan${
        caseSolutionId ? qs.stringify({caseSolutionId}, {addQueryPrefix: true}) : ''
      }`,
  },
  verification: {
    form: (params?: {
      country?: string
      countrySpecific?: string
      brandId?: string
      amazonBrandRegistry?: boolean
    }) =>
      `/verification${
        !_.isEmpty(_.omitBy(params, _.isNil)) ? qs.stringify(params, {addQueryPrefix: true}) : ''
      }`,
    success: (id: string, createAccount?: boolean) =>
      `/verification/${id}/success${
        createAccount ? qs.stringify({createAccount}, {addQueryPrefix: true}) : ''
      }`,
  },
  goodsAndServices: (id: string) => `/registration-request/${id}/goods-and-services`,
  internationalTm: () => '/international-trademark',
  legalNotice: () => '/legal-notice',
  bookConsultation: (id?: string, type?: ConsultationType) =>
    `/book-a-free-consultation/${
      id || type ? qs.stringify({id, type}, {addQueryPrefix: true}) : ''
    }`,
  trademarkStrategy: () => '/trademark-strategy',
  legalServices: () => '/legal-services',
  successLegalServices: () => '/success-legal-services',
  tmRepresentation: () => '/trademark-representation',
  domainResolution: () => '/domain-resolution',
  classAssist: {
    industry: (industry = '-', sector = '-') =>
      industry === '-' && sector === '-' ? '/class-assist' : `/class-assist/${industry}/${sector}`,
    class: (id: string, item?: string) =>
      `/class-assist/trademark-class/${id}${item ? `/${item}` : ''}`,
  },
  tmAcademy: {
    home: () => '/trademark-academy',
    article: (slug: string) => `/trademark-academy/article/${slug}`,
  },
  product: {
    checkout: (id?: string, query?: Record<string, any>) =>
      `${id ? `/product/${id}/checkout` : `/product/checkout`}${
        query ? qs.stringify(query, {addQueryPrefix: true}) : ''
      }`,
    success: (id: string) => `/product/${id}/success`,
    documents: (id: string) => `/product/${id}/documents`,
  },
  landingPage: (country: Jurisdiction) => `/country/${countryCards[country].slug}`,
  howItWorks: () => '/trademark-registration-with-trama',
  trademarkSearch: () => '/trademark-search',
  trademarkOwnershipTransfer: {
    home: () => '/trademark-ownership-transfer',
    form: (params: Record<string, any> = {}) =>
      `/trademark-ownership-transfer/form${qs.stringify(params, {addQueryPrefix: true})}`,
    checkout: (id: string) => `/trademark-ownership-transfer/form/${id}`,
    success: (id: string) => `/trademark-ownership-transfer/form/${id}/success`,
  },
  trademarkRenewal: {
    home: (jurisdiction?: Jurisdiction) =>
      `/trademark-renewal${jurisdiction ? `/${countryCards[jurisdiction].slug}` : ''}`,
    form: (params: Record<string, any> = {}) =>
      `/trademark-renewal/form${qs.stringify(params, {addQueryPrefix: true})}`,
    checkout: (id: string) => `/trademark-renewal/form/${id}`,
    success: (id: string) => `/trademark-renewal/form/${id}/success`,
  },
  declarationOfUse: () => '/declaration-of-use-in-us',
  statementOfUse: () => '/statement-of-use-in-us',
  trademarkQA: {
    home: () => '/trademark-questions-and-answers',
    category: (category: string) => `/trademark-questions-and-answers/${category}`,
    question: (category: string, question: string) =>
      `/trademark-questions-and-answers/${category}/${question}`,
  },
  brandProtectionQA: {
    home: () => '/brand-protection-questions-and-answers',
    category: (category: string) => `/brand-protection-questions-and-answers/${category}`,
    question: (category: string, question: string) =>
      `/brand-protection-questions-and-answers/${category}/${question}`,
  },
  countryGuide: (country?: string) => `/country-guide${country ? `/${country}` : ''}`,
  brandProtectionGuide: (platform?: string) =>
    `/brand-protection-guide${platform ? `/${platform}` : ''}`,
  reportViolation: (trademarkName: string, id: string) =>
    `/report-violation/${trademarkName}/${id}`,
  officeActionResponse: () => '/office-action-response',
  oppositionResponse: () => '/opposition-response',
  trademarkDisputes: {
    home: () => '/trademark-disputes',
    jurisdiction: (jurisdiction?: string) =>
      `/trademark-disputes/jurisdiction${jurisdiction ? `/${jurisdiction}` : ''}`,
    industry: (industry?: string) =>
      `/trademark-disputes/industry${industry ? `/${industry}` : ''}`,
    subjectMatter: (subjectMatter?: string) =>
      `/trademark-disputes/subject-matter${subjectMatter ? `/${subjectMatter}` : ''}`,
    judicialParty: (judicialParty?: string) =>
      `/trademark-disputes/judicial-party${judicialParty ? `/${judicialParty}` : ''}`,
    ipCase: (ipCase: string) => `/trademark-disputes/case/${ipCase}`,
  },
  amazonBrandRegistry: () => '/amazon-brand-registry',
  trademarkDictionary: (term?: string) => `/trademark-dictionary${term ? `/${term}` : ''}`,
  ceaseAndDesistLetter: () => '/cease-and-desist-letter',
  ceaseAndDesistLetterResponse: () => '/cease-and-desist-letter-response',
  coexistenceAgreement: () => '/trademark-coexistence-agreement',
  limitationOfGoodsAndServices: () => '/limitation-of-goods-and-services',
  filingOpposition: () => '/filing-opposition',
  officeAction: {
    landingPage: (id: string) => `/office-action/${id}`,
    additionalInfo: (id: string) => `/office-action/${id}/additional-info`,
  },
  privacyPolicy: () => '/privacy-policy',
  trademarkHallOfFame: {
    home: () => '/trademark-hall-of-fame',
    slug: (slug: string) => `/trademark-hall-of-fame/${slug}`,
  },
  specimenGuide: {
    home: () => '/specimen-guide',
    class: (id: string) => `/specimen-guide/trademark-class/${id}`,
    sector: (id: string) => `/specimen-guide/sector/${id}`,
  },
  brandMonitoring: {
    home: () => '/brand-protection',
    platform: (platform: string) => `/brand-protection/${platform}`,
    order: (id?: string, tier?: BrandSubscriptionTier, caseSolutionId?: string) =>
      `/brand-monitoring-order${id ? '/' + id : ''}${
        tier || caseSolutionId ? qs.stringify({tier, caseSolutionId}, {addQueryPrefix: true}) : ''
      }`,
    success: (id: string) => `/brand-monitoring-order/${id}/success`,
  },
  feedbackFromEmail: (feedback?: EmailFeedback, id?: string) =>
    `/feedback-from-email${
      feedback || id ? qs.stringify({feedback, id}, {addQueryPrefix: true}) : ''
    }`,
  reportBrandInfringement: () => '/report-brand-infringement',
  monitoringResult: (id: string) => `/monitoring-result/${id}`,
  useInCommerceRequest: (id: string) => `/use-in-commerce-request/${id}`,
  industryCaseStudy: (industry: string) => `/industry-case-study/${industry}`,
  unsubscribe: (token: string) => `/unsubscribe/${token}`,
  industryGuide: (industry: IndustryGuideType) => `/industry-guide/${industry}`,
  meetTrama: () => '/meet-trama',
  forLawFirms: (section?: LawFirmsServiceId) => `/for-law-firms${section ? `#${section}` : ''}`,
  engagementLetter: () => '/engagement-letter',
  trademarkManagement: {
    b2c: () => '/trademark-management',
    b2b: () => '/trademark-management-for-law-firms',
  },
  wipoCalculator: () => '/wipo-calculator',
}

export const getRouteToPrivacyPolicy = (locale: Locale) =>
  locale === Locale.en || locale === Locale.es ? routeTo.privacyPolicy() : routeTo.gdpr()
